import { useState, useEffect } from "react"
import { authApi, schoolsApi, coursesApi, referralsApi, onboardingApi } from "../../../services/api"
import OnboardingModal from "../../../components/onboarding/OnboardingModal"
import Button from "../../../components/ui/Button"
import Card, { CardBody } from "../../../components/ui/Card"
import { Field, Input, Select } from "../../../components/ui/Field"
import { useTheme } from "../../../context/ThemeContext"

const NIGERIAN_STATES = [
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "FCT",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara"
]

const Account = ({ onboardingMode = false, onSkipOnboarding, onCompleteOnboarding }) => {
  const { mode, setMode } = useTheme()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [schools, setSchools] = useState([])
  const [courses, setCourses] = useState([])
  const [schoolId, setSchoolId] = useState("")
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    school: "",
    department: "",
    level: "",
    state: "",
    matric: "",
    referralCode: "",
    bankName: "",
    accountName: "",
    accountNumber: ""
  })

  const loadProfile = async () => {
    setLoading(true)
    setError("")
    try {
      const [meRes, schoolsRes] = await Promise.all([authApi.me(), schoolsApi.list()])
      const schoolList = schoolsRes?.data?.data || []
      setSchools(schoolList)
      if (meRes?.data?.data) {
        const p = meRes.data.data
        setProfile(p)
        setForm({
          name: p.name || "",
          email: p.email || "",
          phoneNumber: p.phoneNumber || "",
          school: p.school || "",
          department: p.department || "",
          level: p.level || "",
          state: p.state || "",
          matric: p.matric || "",
          referralCode: p.referralCode || "",
          bankName: p.bankDetails?.bankName || "",
          accountName: p.bankDetails?.accountName || "",
          accountNumber: p.bankDetails?.accountNumber || ""
        })
        const schoolMatch = schoolList.find(s => (s.name || "").trim() === (p.school || "").trim())
        if (schoolMatch) setSchoolId(schoolMatch._id)
      }
    } catch (e) {
      if (e?.response?.status === 401) return
      setError(e?.response?.data?.message || "Failed to load profile")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadProfile()
  }, [])

  useEffect(() => {
    coursesApi.list().then(res => setCourses(res?.data?.data || [])).catch(() => setCourses([]))
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    setError("")
    setSuccess("")
  }

  const handleSchoolSelect = (e) => {
    const id = e.target.value
    setSchoolId(id === "_current" ? "" : id)
    if (id === "_current") return
    const school = schools.find(s => s._id === id)
    setForm(prev => ({ ...prev, school: school?.name || "", department: "" }))
  }

  const handleDepartmentSelect = (e) => {
    setForm(prev => ({ ...prev, department: e.target.value }))
  }

  const handleSave = async (e) => {
    e?.preventDefault()
    setSaving(true)
    setError("")
    setSuccess("")
    try {
      const trimmedBankName = (form.bankName || "").trim()
      const trimmedAccountName = (form.accountName || "").trim()
      const trimmedAccountNumber = (form.accountNumber || "").trim()
      const hasAnyBankField = !!(trimmedBankName || trimmedAccountName || trimmedAccountNumber)
      await authApi.updateProfile({
        name: form.name,
        phoneNumber: form.phoneNumber,
        school: form.school,
        department: form.department,
        level: form.level,
        state: form.state,
        matric: form.matric,
        ...(hasAnyBankField
          ? {
              bankDetails: {
                bankName: trimmedBankName,
                accountName: trimmedAccountName,
                accountNumber: trimmedAccountNumber
              }
            }
          : {})
      })
      setSuccess("Profile updated successfully.")
      if (onboardingMode) {
        await onboardingApi.complete()
      }
      if (onboardingMode && onCompleteOnboarding) onCompleteOnboarding()
      await loadProfile()
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save profile")
    } finally {
      setSaving(false)
    }
  }

  const handleGenerateReferralCode = async () => {
    setSaving(true)
    setError("")
    setSuccess("")
    try {
      const res = await referralsApi.generateCode()
      const generatedCode = res?.data?.data?.referralCode || ""
      if (generatedCode) {
        setForm((prev) => ({ ...prev, referralCode: generatedCode }))
        setSuccess("Referral code generated successfully.")
      }
      await loadProfile()
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to generate referral code")
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="p-8">Loading profile...</div>

  const needsOnboarding = profile?.onboardingStatus !== 'COMPLETED'
  const displayName = (form.name || profile?.name || "").trim() || "Student"
  const displayEmail = (form.email || profile?.email || "").trim() || ""
  const studentInitial = (displayName.trim()[0] || "?").toUpperCase()
  const onboardingPillLabel =
    profile?.onboardingStatus === "COMPLETED" ? "Onboarding complete" : "Action needed"

  return (
    <section className="space-y-5">
      <OnboardingModal
        open={onboardingOpen}
        initialProfile={profile}
        onClose={() => setOnboardingOpen(false)}
        onRefetch={loadProfile}
      />

      <Card className="w-full shadow-soft overflow-hidden">
        <div className="h-28 bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-400" />

        <div className="relative px-6 sm:px-8 -mt-12 pb-5">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between items-start gap-5">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-full bg-white border border-emerald-100 shadow-sm flex items-center justify-center">
                <span className="text-3xl font-bold text-emerald-700">{studentInitial}</span>
              </div>
              <div className="min-w-0">
                
                <h1 className="mt-0 text-xl sm:text-2xl font-bold text-slate-900 leading-tight truncate">
                  {displayName}
                </h1>
                <p className="text-sm text-slate-600 mt-3 truncate">{displayEmail || "—"}</p>
              </div>
            </div>

          </div>
        </div>

        <CardBody className="pt-10">
          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-800 mb-5">
              <div className="flex items-start gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm font-semibold">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 mb-5">
              <div className="flex items-start gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 8.707 9.879a1 1 0 00-1.414 1.414l1 1a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm font-semibold">{success}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-1 space-y-4">
              <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Quick summary</p>
                    <p className="text-xs text-slate-600 mt-1">Updates reflect as you edit.</p>
                  </div>
                  <div className="h-9 w-9 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75V12l3.75 2.25M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-500">School</span>
                    <span className="text-sm font-semibold text-slate-900 truncate">{form.school || profile?.school || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-500">Department</span>
                    <span className="text-sm font-semibold text-slate-900 truncate">{form.department || profile?.department || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-500">Level</span>
                    <span className="text-sm font-semibold text-slate-900">{form.level || profile?.level || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-500">State</span>
                    <span className="text-sm font-semibold text-slate-900">{form.state || profile?.state || "—"}</span>
                  </div>
                </div>

              </div>

              

              {onboardingMode ? (
                <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50 p-5">
                  <p className="text-sm font-semibold text-emerald-900">Complete profile (optional)</p>
                  <p className="text-xs text-emerald-800 mt-1">
                    You can skip for now and continue to your dashboard.
                  </p>
                </div>
              ) : null}
            </div>

            <form onSubmit={handleSave} className="lg:col-span-2 space-y-5">
              <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Personal</p>
                    <p className="text-xs text-slate-600 mt-1">Basic information used across your account.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <Field label="Name" className="sm:col-span-2">
                    <Input type="text" name="name" value={form.name} onChange={handleChange} />
                  </Field>
                  <Field label="Email" hint="Email cannot be changed here" className="sm:col-span-2">
                    <Input type="email" name="email" value={form.email} readOnly disabled />
                  </Field>
                  <Field label="Phone Number">
                    <Input
                      type="tel"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      placeholder="08012345678"
                    />
                  </Field>
                  <Field label="Matric Number">
                    <Input type="text" name="matric" value={form.matric} onChange={handleChange} />
                  </Field>
                  <Field label="Your referral code" hint="Share this to earn ₦2,000 per active referral" className="sm:col-span-2">
                    <div className="flex gap-2">
                      <Input type="text" name="referralCode" value={form.referralCode} readOnly disabled />
                      {(form.referralCode || "").trim() ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const code = (form.referralCode || "").trim()
                            if (!code) return
                            navigator.clipboard?.writeText(code).catch(() => {})
                            setSuccess("Referral code copied.")
                          }}
                        >
                          Copy
                        </Button>
                      ) : (
                        <Button type="button" variant="outline" onClick={() => void handleGenerateReferralCode()} loading={saving}>
                          Generate code
                        </Button>
                      )}
                    </div>
                  </Field>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Bank details</p>
                <p className="text-xs text-slate-600 mt-1">Used for referral payouts.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <Field label="Bank">
                    <Input type="text" name="bankName" value={form.bankName} onChange={handleChange} placeholder="e.g. Access Bank" />
                  </Field>
                  <Field label="Account number">
                    <Input type="text" name="accountNumber" value={form.accountNumber} onChange={handleChange} placeholder="10 digits" />
                  </Field>
                  <Field label="Account name" className="sm:col-span-2">
                    <Input type="text" name="accountName" value={form.accountName} onChange={handleChange} placeholder="e.g. John Doe" />
                  </Field>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Academic</p>
                <p className="text-xs text-slate-600 mt-1">These details help generate more accurate outputs.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <Field label="Level">
                    <Select name="level" value={form.level} onChange={handleChange}>
                      <option value="">Select level</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                      <option value="300">300</option>
                      <option value="400">400</option>
                      <option value="500">500</option>
                      <option value="Masters">Masters</option>
                      <option value="PhD">PhD</option>
                    </Select>
                  </Field>

                  <Field label="State">
                    <Select name="state" value={form.state} onChange={handleChange}>
                      <option value="">Select state</option>
                      {NIGERIAN_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="School" className="sm:col-span-2">
                    <Select value={schoolId || (form.school ? "_current" : "")} onChange={handleSchoolSelect}>
                      <option value="">Select school</option>
                      {schools.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.name}
                        </option>
                      ))}
                      {form.school &&
                        !schools.some((s) => (s.name || "").trim() === (form.school || "").trim()) &&
                        form.school.trim() && <option value="_current">{form.school}</option>}
                    </Select>
                  </Field>

                  <Field label="Department / Course" className="sm:col-span-2">
                    <Select name="department" value={form.department} onChange={handleDepartmentSelect}>
                      <option value="">Select department</option>
                      {courses.map((c) => (
                        <option key={c._id} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                      {form.department &&
                        !courses.some((c) => c.name === form.department) &&
                        form.department.trim() && <option value={form.department}>{form.department}</option>}
                    </Select>
                  </Field>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                <p className="text-xs text-slate-500">Changes are saved to your profile.</p>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <Button type="submit" className="w-full sm:w-auto" loading={saving}>
                    Save changes
                  </Button>
                  {onboardingMode && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => onSkipOnboarding && onSkipOnboarding()}
                    >
                      Skip for now
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-soft">
          <CardBody className="py-5 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Delete Account</h3>
              <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 p-4">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white border border-rose-200">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-rose-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M5 7h14M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-1 0l1 14a1 1 0 01-1 1H8a1 1 0 01-1-1L8 7z" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-rose-800">Warning</p>
                    <p className="text-sm text-rose-700 mt-1">
                      This action cannot be undone. If you need help, contact support before deleting.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Button
              type="button"
              variant="danger"
              className="w-full"
              onClick={() => document.getElementById("delete_account_modal").showModal()}
            >
              Delete account
            </Button>
          </CardBody>
        </Card>
      </div>

      <dialog id="delete_account_modal" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Delete Account</h3>
          <p className="py-4">This action cannot be undone. Contact support if you need to delete your account.</p>
          <div className="modal-action">
            <form method="dialog" className="w-full flex justify-between">
              <Button
                type="button"
                variant="danger"
                onClick={() => document.getElementById("delete_account_modal").close()}
              >
                Confirm (not implemented)
              </Button>
              <Button type="button" variant="outline" onClick={() => document.getElementById("delete_account_modal").close()}>
                Close
              </Button>
            </form>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button type="button">close</button>
        </form>
      </dialog>
    </section>
  )
}

export default Account